import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate } from 'react-router-dom';
import { ROUTES } from 'constants/routes';
import { LEGAL_CONFIG } from 'config/legal';
import { setAccessToken } from 'services/api';
import { getTermsStatus, submitTermsAcceptance } from 'services/termsService';

// Redirecting to Auth0 alone never constitutes acceptance of Rely's terms.
export default function SignupTermsAcceptance() {
  const { getAccessTokenSilently } = useAuth0();
  const [status, setStatus] = useState('loading');
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const termsVersion = LEGAL_CONFIG.documents.terms.version;
  const privacyVersion = LEGAL_CONFIG.documents.privacy.version;

  useEffect(() => {
    let active = true;
    async function checkAcceptance() {
      try {
        const token = await getAccessTokenSilently();
        if (!active) return;
        if (!token) throw new Error('Authentication required');
        setAccessToken(token);
        const current = await getTermsStatus();
        if (active) {
          setStatus(
            current.hasAcceptedTerms &&
              current.termsVersion === termsVersion &&
              current.privacyVersion === privacyVersion
              ? 'complete'
              : 'pending'
          );
        }
      } catch (_error) {
        if (active) setStatus('error');
      }
    }
    checkAcceptance();
    return () => {
      active = false;
    };
  }, [getAccessTokenSilently, termsVersion, privacyVersion, attempt]);

  async function acceptTerms(event) {
    event.preventDefault();
    if (!accepted || saving) return;
    setSaving(true);
    setSaveFailed(false);
    try {
      const token = await getAccessTokenSilently();
      if (!token) throw new Error('Authentication required');
      setAccessToken(token);
      const result = await submitTermsAcceptance({
        acceptedAt: new Date().toISOString(),
        termsVersion,
        privacyVersion,
        userAgent: navigator.userAgent,
      });
      if (result?.success !== true)
        throw new Error('Terms acceptance was not saved');
      setStatus('complete');
    } catch (_error) {
      setSaveFailed(true);
    } finally {
      setSaving(false);
    }
  }

  if (status === 'complete') return <Navigate to={ROUTES.HOME} replace />;

  return (
    <main className="flex min-h-screen items-center justify-center bg-neutral-50 p-6 text-neutral-900 dark:bg-neutral-950 dark:text-white">
      {status === 'loading' ? (
        <p role="status">Preparando tu cuenta…</p>
      ) : status === 'error' ? (
        <div className="space-y-4 text-center">
          <p role="alert">No pudimos verificar tu sesión.</p>
          <button
            type="button"
            className="min-h-[44px] underline"
            onClick={() => {
              setStatus('loading');
              setAttempt(attempt + 1);
            }}
          >
            Reintentar
          </button>
        </div>
      ) : (
        <form
          onSubmit={acceptTerms}
          className="w-full max-w-md space-y-6 rounded-2xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-900"
        >
          <h1 className="text-xl font-semibold">Antes de comenzar</h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-300">
            Revisá y aceptá los términos para continuar en Rely.
          </p>
          <div className="flex items-start gap-3 text-sm">
            <input
              id="signup-terms"
              type="checkbox"
              checked={accepted}
              disabled={saving}
              onChange={(event) => setAccepted(event.target.checked)}
              className="mt-1 h-4 w-4 shrink-0"
            />
            <label htmlFor="signup-terms">
              He leído y acepto los{' '}
              <a
                className="underline"
                href={LEGAL_CONFIG.urls.terms}
                target="_blank"
                rel="noreferrer"
              >
                Términos de Servicio
              </a>{' '}
              y la{' '}
              <a
                className="underline"
                href={LEGAL_CONFIG.urls.privacy}
                target="_blank"
                rel="noreferrer"
              >
                Política de Privacidad
              </a>
              .
            </label>
          </div>
          {saveFailed && (
            <p role="alert" className="text-sm text-red-700 dark:text-red-300">
              No pudimos guardar tu aceptación. Intentá nuevamente.
            </p>
          )}
          <button
            type="submit"
            disabled={!accepted || saving}
            className="min-h-[44px] w-full rounded-lg bg-[#71DE80] px-5 font-medium text-neutral-950 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Guardando…' : 'Continuar'}
          </button>
        </form>
      )}
    </main>
  );
}
